import { AGE_GROUPS } from "../config/marketplaceFilterEngine";
import {
  caregiverOffersAttendantPackage,
  resolveOfferedShiftTypesFromPricing,
} from "./attendantPackageOffer";

function norm(s) {
  return String(s || "")
    .trim()
    .toLowerCase();
}

/** Normalize skill/condition labels for fuzzy marketplace matching. */
function normalizeMatchText(s) {
  return norm(s)
    .replace(/['’]/g, "")
    .replace(/&/g, " and ")
    .replace(/[^a-z0-9\s/]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

/**
 * Map filter-panel labels → common caregiver skill phrasings in profiles.
 * Without this, "Elderly Care" fails to match stored "Elder Care", etc.
 */
const FILTER_TERM_ALIASES = {
  "elderly care": ["elder care", "elderly", "senior care", "elder"],
  bedridden: ["bedridden care"],
  "post surgery": ["post surgery care", "post-surgery care", "postsurgery care", "post operative", "postoperative"],
  alzheimers: ["alzheimer", "alzheimer / dementia", "alzheimers care"],
  dementia: ["alzheimer / dementia", "dementia care", "alzheimers"],
  parkinsons: ["parkinson", "parkinsons care"],
  "feeding tube": ["peg feed", "feeding assistance"],
  "catheter care": ["catheter"],
  "mobility support": ["mobility", "mobility and transfer", "mobility & transfer", "wheelchair transfer"],
  "feeding assistance": ["feeding", "peg feed"],
};

function expandMatchTerms(term) {
  const n = normalizeMatchText(term);
  if (!n) return [];
  const aliases = FILTER_TERM_ALIASES[n] || FILTER_TERM_ALIASES[n.replace(/s$/, "")] || [];
  const out = new Set([n]);
  aliases.forEach((a) => {
    const v = normalizeMatchText(a);
    if (v) out.add(v);
  });
  return [...out];
}

function listFromCaregiver(c) {
  const skills = Array.isArray(c.skills)
    ? c.skills
    : String(c.skills || "")
        .split(",")
        .map((x) => x.trim())
        .filter(Boolean);
  const languages = Array.isArray(c.languages)
    ? c.languages
    : String(c.languages || "")
        .split(",")
        .map((x) => x.trim())
        .filter(Boolean);
  return { skills, languages };
}

function caregiverAge(c) {
  const age = Number(c.age);
  if (Number.isFinite(age) && age > 0) return age;
  if (c.date_of_birth || c.dateOfBirth) {
    const d = new Date(c.date_of_birth || c.dateOfBirth);
    if (!Number.isNaN(d.getTime())) {
      const today = new Date();
      let years = today.getFullYear() - d.getFullYear();
      const m = today.getMonth() - d.getMonth();
      if (m < 0 || (m === 0 && today.getDate() < d.getDate())) years -= 1;
      return years;
    }
  }
  return null;
}

function isVerified(c) {
  const kyc = norm(c.kycStatus || c.aadhaar_kyc);
  const police = norm(c.policeVerification || c.police_verification);
  const approved = c.adminApproved ?? c.admin_approved;
  return (
    ["verified", "approved", "complete"].includes(kyc) &&
    ["verified", "approved", "complete"].includes(police) &&
    approved !== false
  );
}

/** Fuzzy multi-select: selected filter values vs caregiver skill/language lists. */
export function matchesMultiSelect(selected, haystack) {
  if (!selected?.length) return true;
  const hay = (haystack || []).map(normalizeMatchText).filter(Boolean);
  if (!hay.length) return false;
  return selected.some((sel) => {
    const terms = expandMatchTerms(sel);
    return terms.some((t) => hay.some((h) => h === t || h.includes(t) || t.includes(h)));
  });
}

function workTypeLabelToShiftType(label) {
  const raw = String(label || "").trim();
  if (!raw) return "";
  const lower = raw.toLowerCase();
  if (lower === "hourly" || (lower.includes("hour") && !lower.includes("12") && !lower.includes("24"))) {
    return "hourly";
  }
  if (lower.includes("12") && (lower.includes("night") || lower.includes("night shift"))) {
    return "night";
  }
  if (lower.includes("12") && (lower.includes("day") || lower.includes("day shift"))) {
    return "day";
  }
  if (lower.includes("24") || lower.includes("live-in") || lower.includes("live in")) {
    return "24hour";
  }
  if (lower === "full-time" || lower === "full time") return "24hour";
  if (lower === "part-time" || lower === "part time" || lower === "live-out" || lower === "live out") {
    return "hourly";
  }
  if (lower === "night") return "night";
  if (lower === "day") return "day";
  if (lower === "hourly") return "hourly";
  if (lower === "24hour" || lower === "24h") return "24hour";
  return "";
}

function resolveCaregiverWorkTypes(c) {
  if (Array.isArray(c.workTypes) && c.workTypes.length) {
    return c.workTypes.map((s) => String(s).trim()).filter(Boolean);
  }
  const raw = c.workType || c.availabilitySummary?.workType || c.availability || "";
  if (Array.isArray(raw)) return raw.map((s) => String(s).trim()).filter(Boolean);
  return String(raw || "")
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
}

/**
 * A caregiver can offer a shift type via any of several independent data
 * sources (Pricing & Plans rows, an explicit shiftTypes list, or the
 * Availability "work type" badges) — merge all of them instead of trusting
 * only whichever source happens to be populated first, since e.g. having an
 * hourly package priced shouldn't hide day/night/live-in shifts enabled via
 * the Availability badges.
 */
function inferShiftTypes(c) {
  const out = new Set();
  resolveOfferedShiftTypesFromPricing(c).forEach((s) => {
    const n = norm(s);
    if (n) out.add(n);
  });
  if (Array.isArray(c.shiftTypes)) {
    c.shiftTypes.forEach((s) => {
      const n = norm(s);
      if (n) out.add(n);
    });
  }
  resolveCaregiverWorkTypes(c)
    .map(workTypeLabelToShiftType)
    .forEach((s) => {
      if (s) out.add(s);
    });
  if (c.shiftType) {
    const n = norm(c.shiftType);
    if (n) out.add(n);
  }
  return [...out];
}

function matchesCareType(c, careType, skills) {
  const type = norm(careType);
  if (!type || type === "any") return true;

  const role = norm(c.serviceRole || c.careType);
  if (role === type) return true;

  if (type === "dementia") {
    if (role && !["attendant", "dementia"].includes(role)) return false;
    return matchesMultiSelect(["Dementia", "Alzheimer's"], skills);
  }

  if (type === "post_operative") {
    if (role && !["attendant", "post_operative", "postoperative"].includes(role)) return false;
    return matchesMultiSelect(["Post Surgery", "Palliative Care"], skills);
  }

  if (role && role !== type) return false;
  return true;
}

/**
 * Apply NEMA Filter Engine v1 client-side against mapped caregiver rows.
 */
export function applyMarketplaceFilters(caregivers, filters) {
  if (!Array.isArray(caregivers) || !filters) return caregivers || [];

  return caregivers.filter((c) => {
    const { skills, languages } = listFromCaregiver(c);

    if (filters.careType && filters.careType !== "any") {
      if (!matchesCareType(c, filters.careType, skills)) return false;
    }

    if (filters.patientConditions?.length && !matchesMultiSelect(filters.patientConditions, skills)) {
      return false;
    }

    if (filters.languages?.length) {
      const langMatch = filters.languages.some((lang) =>
        languages.some((l) => norm(l).includes(norm(lang)) || norm(lang).includes(norm(l)))
      );
      if (!langMatch) return false;
    }

    if (filters.gender === "female" && norm(c.gender) !== "female") return false;
    if (filters.gender === "male" && norm(c.gender) !== "male") return false;

    if (filters.ageGroup) {
      const band = AGE_GROUPS.find((a) => a.value === filters.ageGroup);
      const age = caregiverAge(c);
      if (band && age != null && (age < band.min || age > band.max)) return false;
    }

    if (filters.minExperience > 0) {
      const years = Number(c.years ?? c.experience_years ?? c.experienceYears);
      if (!Number.isFinite(years) || years < filters.minExperience) return false;
    }

    if (filters.verifiedOnly && !isVerified(c)) return false;

    if (filters.skills?.length && !matchesMultiSelect(filters.skills, skills)) return false;

    if (filters.religion && filters.religion !== "any") {
      const cgRel = norm(c.religion);
      if (cgRel && cgRel !== norm(filters.religion)) return false;
    }

    if (filters.foodPreference && filters.foodPreference !== "any") {
      const food = norm(c.foodPreference || c.food_preference);
      if (food && food !== norm(filters.foodPreference)) return false;
    }

    if (filters.sleepArrangement) {
      const sleep = norm(c.sleepArrangement || c.sleep_arrangement);
      if (sleep && sleep !== norm(filters.sleepArrangement)) return false;
    }

    if (filters.availableNow && c.isAvailable === false) return false;

    if (filters.minRating > 0) {
      const rating = Number(c.rating);
      if (!Number.isFinite(rating) || rating < filters.minRating) return false;
    }

    if (filters.shiftType) {
      if (!inferShiftTypes(c).includes(norm(filters.shiftType))) return false;
    }

    const hourly = Number(c.hourlyRateExpectation ?? c.hourly_rate_expectation);
    const monthly = Number(c.salaryExpectation ?? c.salary_expectation ?? c.expectedSalary);
    const showHourly = filters.salaryServiceType !== "monthly";
    const showMonthly = filters.salaryServiceType !== "hourly";
    if (showHourly && filters.salaryHourlyMax < 5000 && Number.isFinite(hourly) && hourly > filters.salaryHourlyMax) {
      return false;
    }
    if (showMonthly && filters.salaryMonthlyMax < 100000 && Number.isFinite(monthly) && monthly > filters.salaryMonthlyMax) {
      return false;
    }

    if (filters.careType === "nurses") {
      if (filters.nurseClinical?.length && !matchesMultiSelect(filters.nurseClinical, skills)) return false;
      if (filters.nurseQualifications?.length) {
        const quals = Array.isArray(c.qualifications) ? c.qualifications : skills;
        if (!matchesMultiSelect(filters.nurseQualifications, quals)) return false;
      }
      if (filters.nurseSkills?.length && !matchesMultiSelect(filters.nurseSkills, skills)) return false;
    }

    if (filters.careType === "physio") {
      if (filters.physioRequirements?.length && !matchesMultiSelect(filters.physioRequirements, skills)) return false;
      if (filters.physioFrequency && c.physioFrequency && norm(c.physioFrequency) !== norm(filters.physioFrequency)) {
        return false;
      }
      if (filters.physioDuration && c.physioDuration && norm(c.physioDuration) !== norm(filters.physioDuration)) {
        return false;
      }
      if (filters.consultationType) {
        const wanted = norm(filters.consultationType);
        const offered = Array.isArray(c.consultationTypes)
          ? c.consultationTypes.map(norm)
          : c.consultationType
            ? [norm(c.consultationType)]
            : ["home_visit"];
        if (!offered.includes(wanted)) return false;
      }
    }

    if (
      !caregiverOffersAttendantPackage(c, {
        packageKey: filters.packageKey,
        serviceId: filters.serviceId,
      })
    ) {
      return false;
    }

    return true;
  });
}

export function mergeSpecialisationsFromFilters(filters) {
  const parts = [...(filters.patientConditions || [])];
  if (filters.careType === "nurses") parts.push(...(filters.nurseClinical || []));
  if (filters.careType === "physio") parts.push(...(filters.physioRequirements || []));
  return [...new Set(parts.map((s) => String(s).trim()).filter(Boolean))];
}
